import { ContentBodyTypeEnum } from "@/types"
import type { LookupResult } from "@/types/contents"

import type {
  ContentBodyAttributes,
  TrackFormData,
} from "../_components/content-body-forms"
import { contentBodyAttributes } from "../tracks.constant"

type PostAttributes = TrackFormData["track"]["post_attributes"]

// Maps server response type
const TYPE_MAP: Record<string, ContentBodyTypeEnum> = {
  VideoBody: ContentBodyTypeEnum.video,
  AudioBody: ContentBodyTypeEnum.audio,
  ArticleBody: ContentBodyTypeEnum.article,
  PhotoBody: ContentBodyTypeEnum.photo,
}

/**
 * Build the next post_attributes from a URL lookup result. Pure: only the
 * fields the server returned are overwritten; everything else is left as-is.
 * @param result - A result of the response from the server lookup url
 * @param post - Current post attributes
 * @returns A updated post attributes
 */
export function setPrefillValues(
  result: LookupResult,
  post: PostAttributes,
): PostAttributes {
  // Get prefill data
  const prefill = result.fields

  // Map the body content type safely
  const result_content_type = result.content_body_type
    ? TYPE_MAP[result.content_body_type]
    : undefined
  const type = result_content_type ?? post.content_body_type

  // Reference original content body object
  const originContentBody =
    type === post.content_body_type
      ? post.content_body_attributes
      : contentBodyAttributes[type]

  // Copy the post attributes (content_body_type / _attributes are set on return)
  const newPostAttr: PostAttributes = { ...post }

  // Create a copy content body attribute object for updating with prefill data
  const newContentBodyAttr: ContentBodyAttributes = { ...originContentBody }

  // Safely update each attribute of post
  if (prefill.title != null) newPostAttr.title = prefill.title
  if (prefill.creator != null) newPostAttr.creator = prefill.creator
  if (prefill.thumbnail_url != null)
    newPostAttr.thumbnail_url = prefill.thumbnail_url

  // Safely update each attribute of content body object
  if (result.source != null) newContentBodyAttr.source = result.source
  if (prefill.duration_seconds != null)
    newContentBodyAttr.duration_seconds = prefill.duration_seconds
  if (prefill.aspect != null) newContentBodyAttr.aspect = prefill.aspect
  if (prefill.description != null && type === ContentBodyTypeEnum.article)
    newContentBodyAttr.body = prefill.description

  // Set updated data from prefilling
  return {
    ...newPostAttr,
    content_body_type: type,
    content_body_attributes: newContentBodyAttr,
  }
}
